import React from "react";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";

import { DefaultTheme } from "@mui/private-theming";
import { XGUIProps } from "../utils/interfaces/gui/fields.interfaces";
import { IXImageProps } from "../utils/interfaces/Elements/XImage.interfaces";
import { IXIconProps } from "../utils/interfaces/Elements/XIcon.interfaces";
import { IArticleData } from "../utils/interfaces/common/XArticle.interfaces";
import { IAuthUserFormInput } from "../utils/interfaces/core/AuthUser";

export type TApiFetcherArticlesArgs = {
  businessId?: string;

  id?: string;

  articleName?: string;

  categoryId?: string;

  badgeId?: string;

  /**
   * order by NAME | PRICE | QUANTITY (For limited edition) | RATING | CREATED_AT
   */
  order?: "name" | "price" | "rating" | "qty" | "createdAt";

  /**
   * DIRECTION
   * @default desc
   */
  sort?: "asc" | "desc";

  /**
   * LIMIT IS USED INTERNALY
   * IT IS ASSIGNED BU PAGE LIMIT
   */
  limit?: number;
};

export interface IDataFetching extends TApiFetcherArticlesArgs {
  /**
   * LIST OF CATEGORY IDs
   * ONE FETCH FROM ONE CATEGORIES
   * 2+ CREATE A TABS GRID WITH CATEGORY NAME AS LABEL
   * TODO : TAB STYLE NEED TO BE ADDED
   */
  categories?: string[]; // LIST CATEGORY IDS

  /**
   * LIST OF BADGES IDs
   */
  badges?: string[]; // LIST OF BADGE IDs

  enabled?: boolean;
}

export type TImageComponent = (
  props: IXImageProps,
  env: "dev" | "prod" | "preview",
  devProps: any,
  index: string
) => React.ReactElement | null;

export type TIconComponent = (props?: IXIconProps) => React.ReactNode | null;

export type TAPIHandler = (query?: IDataFetching) => Promise<any> | any;

export interface IXoraiaConfig {
  /**
   * the environment mode
   * "dev" for the dashboard (edtition mode)
   * "prod" for the websote (preview and production mode)
   */
  env?: "prod" | "dev" | "preview";

  /**
   * Pop Up editor (dev mode)
   */
  XGUI?: React.FC<XGUIProps>;

  /**
   * @todo implement the material theming
   */
  theme?: Partial<DefaultTheme>; // TODO : IMPLEMENT THEME

  /**
   * next js component implementation
   */
  imageComponent?: TImageComponent;

  /**
   * @todo global icon implementation
   */
  iconComponent?: TIconComponent;

  /**
   * FUNCTION TO UPLOAD FILE
   */
  uploadFile?: (file: File, type: "image" | "video") => Promise<string>;

  /**
   * domain to show in the browser url
   */
  domain?: string;

  /**
   * connected user token
   */
  userId?: string;

  /**
   * xoraia orgs id or slug
   */
  businessId: string;

  /**
   * THIS IS USED FOR THE QUERY FILTER ON PAGES
   */
  setUrlParams?: TSetUrlParams;

  /**
   * FECTH DATA FROM API ENDPOINT
   */
  apiFetcher?: TApiFetcher;

  /**
   * THE LINK COMPONENT EITHER A | NEXT/LINK
   */
  Link: React.FC<{
    href?: string;
    children?: React.ReactNode;
    target?: "_blank";
    as?: string;
  }>;

  /**
   * ARTICLE DATA TO USE PRODUCT DETAILS
   */
  article?: IArticleData;
}

export interface ICategoryData {
  name: string;
  id: string;
}

export interface IBadgeData {
  name: string;
  id: string;
}

export interface IOptionData {
  name: string;
  id: string;
  values: string[];
  type: "SELECT" | "COLOR" | "CHOICE";
}

export type TApiFetcher = {
  articles?: (args?: TApiFetcherArticlesArgs) => Promise<IArticleData[]>;

  categories?: (args?: any) => Promise<ICategoryData[]>;

  badges?: (args?: any) => Promise<IBadgeData[]>;

  options?: (args?: any) => Promise<IOptionData[]>;

  /**
   * use null as args to logout user
   */
  setAuthUser?: (args?: IAuthUserFormInput | null) => Promise<any>;
};

// TODO CHECK DATA TYPE LATER
export type TSetUrlParamsArgs = {
  /**
   * search query
   */
  q?: string;

  /**
   * min price
   */
  mn?: number;

  /**
   * max price
   */
  mx?: number;

  /**
   * category slug | id
   */
  c?: string;

  /**
   * tags id | slug
   */
  t?: string[];

  /**
   * sort createdAt | name | price | rating
   */
  s?: "createdAt" | "name" | "price" | "rating";

  /**
   * order asc | desc
   */
  o?: "asc" | "desc";

  /**
   * Current Page
   */
  p?: number;

  /**
   * article slug | id
   */
  a?: string;

  /**
   * article quantity used on product details
   */
  qty?: number;

  /**
   * any dynamic options
   */
  [key: string]: any;
};

export type TSetUrlParams = (args: TSetUrlParamsArgs) => Promise<{
  articles: IArticleData[];
  config: TSetUrlParams;
  count?: number;
}>;

const USE_XORAIA = atom<IXoraiaConfig>({
  key: "USE_XORAIA",

  default: {
    env: "prod",
    businessId: "LOCALHOST",
    Link: (props) => <a {...props}>{props.children}</a>,
  },
});

export const useXoraia = () => useRecoilState(USE_XORAIA);

export const useXoraiaValue = () => useRecoilValue(USE_XORAIA);

export const useXoraiaUpdater = () => useSetRecoilState(USE_XORAIA);
